import React, { useState, useRef, useEffect, useContext } from "react";
import { useTheme } from "@material-ui/core/styles";

import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import useSound from "use-sound";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import ChatIcon from "@material-ui/icons/Chat";

import TicketListItem from "../TicketListItemCustom";
import { i18n } from "../../translate/i18n";
import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import Favicon from "react-favicon";
import { getBackendURL } from "../../services/config";
import useSettings from "../../hooks/useSettings";

const defaultLogoFavicon = "/vector/favicon.svg";

const useStyles = makeStyles((theme) => ({
  tabContainer: {
    overflowY: "auto",
    maxHeight: 400,
    padding: theme.spacing(1),
    backgroundColor: theme.palette.type === "dark" ? "#131614" : "#ffffff",
    width: "100%",
    direction: "ltr",
    textAlign: "left",
    ...theme.scrollbarStyles,
  },
  popoverPaper: {
    width: "100%",
    maxWidth: 380,
    borderRadius: 16,
    overflow: "hidden",
    backgroundColor: theme.palette.type === "dark" ? "#131614" : "#ffffff",
    boxShadow: theme.palette.type === "dark"
      ? '0 15px 50px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.08)'
      : '0 15px 50px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
    direction: "ltr",
    textAlign: "left",
    [theme.breakpoints.down("sm")]: {
      maxWidth: 300,
    },
  },
  popoverHeader: {
    padding: theme.spacing(2, 2.5),
    background: theme.palette.type === "dark"
      ? 'linear-gradient(135deg, #131614 0%, #334155 100%)'
      : 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
    color: '#fff',
    fontWeight: 600,
    fontSize: 15,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  noShadow: {
    boxShadow: "none !important",
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4, 2),
    color: theme.palette.type === "dark" ? 'rgba(255,255,255,0.5)' : '#9ca3af',
    fontSize: 14,
  },
}));

const NotificationsPopOver = (props) => {
  const classes = useStyles();
  const theme = useTheme();

  const history = useHistory();
  const { user } = useContext(AuthContext);
  const ticketIdUrl = +history.location.pathname.split("/")[2];
  const ticketIdRef = useRef(ticketIdUrl);
  const anchorEl = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  // Tracks the tenant identity this component last rendered for. When
  // user/companyId flips (in-tab account switch), we wipe the local
  // notifications buffer so the previous tenant's socket-pushed
  // tickets don't survive into the new session. See the merge logic
  // below — without this guard, `prevState` keeps tickets that aren't
  // in the new fetch and there's no socket `delete` to remove them.
  const tenantKeyRef = useRef(null);
  const [soundGroupNotifications, setSoundGroupNotifications] = useState(false);
  const [showTabGroups, setShowTabGroups] = useState(false);
  const { profile, queues } = user;

  const [, setDesktopNotifications] = useState([]);

  // Fetch tickets with unread messages, filtered by user's queues
  const queueIds = queues?.map((q) => q.id) || [];
  const { tickets } = useTickets({
    withUnreadMessages: "true",
    showAll: (profile === "admin" || profile === "supervisor") ? "true" : "false",
    queueIds: (profile === "admin" || profile === "supervisor") ? [] : queueIds,
  });

  // Fetch pending tickets for the favicon badge count. Raviv 2026-05-30:
  // the old badge showed total unread-message count (sum of unreadMessages
  // across all notification tickets), which had nothing to do with the
  // "ממתינות" count the user sees in the sidebar. Now the badge tracks
  // pending tickets one-to-one.
  const { tickets: pendingTickets } = useTickets({
    status: "pending",
    showAll: (profile === "admin" || profile === "supervisor") ? "true" : "false",
    queueIds: (profile === "admin" || profile === "supervisor") ? [] : queueIds,
  });
  const [play] = useSound(alertSound, {volume: props.volume});
  const soundAlertRef = useRef();
  const { getSetting } = useSettings();

  const historyRef = useRef(history);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    getSetting("soundGroupNotifications").then((soundGroupNotifications) => {
      setSoundGroupNotifications(soundGroupNotifications === "enabled");
    });

    Promise.all([
      getSetting("CheckMsgIsGroup"),
      getSetting("groupsTab")
    ]).then(([ignoreGroups, groupsTab]) => {
      setShowTabGroups(ignoreGroups === "disabled" && groupsTab === "enabled");
    });
  }, []);

	useEffect(() => {
		soundAlertRef.current = play;

		if (!("Notification" in window)) {
			console.log("This browser doesn't support notifications");
		} else {
			Notification.requestPermission();
		}
	}, [play]);

	useEffect(() => {
		// Cross-tenant guard: if companyId/userId changed since last render,
		// drop the buffered notifications — they belong to a stale session.
		const currentTenant = `${localStorage.getItem("companyId") || ""}:${localStorage.getItem("userId") || ""}`;
		if (tenantKeyRef.current !== currentTenant) {
			tenantKeyRef.current = currentTenant;
			setNotifications([]);
			setDesktopNotifications(prev => {
				prev.forEach(n => { try { n.close(); } catch { /* already closed */ } });
				return [];
			});
		}
	}, [user, profile]);

	useEffect(() => {
		// Filter tickets based on user permissions (same logic as socket events)
		const filteredTickets = tickets.filter(ticket => {
			const isAdmin = profile === "admin" || profile === "supervisor";
			const ticketQueueId = ticket.queueId;
			const ticketUserId = ticket.userId;

			// Admin sees all
			if (isAdmin) return true;

			// Ticket is assigned to this user
			if (ticketUserId === user?.id) return true;

			// Ticket is unassigned and in user's queue (or no queue)
			const userQueueIds = queues?.map(q => q.id) || [];
			const isInUserQueue = !ticketQueueId || userQueueIds.includes(ticketQueueId);
			if (!ticketUserId && isInUserQueue) return true;

			return false;
		});

		// Merge with existing notifications instead of replacing
		// This preserves socket-added notifications that might not be in useTickets yet
		setNotifications(prevState => {
			const existingIds = new Set(filteredTickets.map(t => t.id));
			// Keep socket-added tickets that aren't in the new fetch
			const socketAddedTickets = prevState.filter(t => !existingIds.has(t.id));
			// Combine: new fetch + socket-added (deduped)
			const merged = [...filteredTickets];
			socketAddedTickets.forEach(ticket => {
				if (!merged.find(t => t.id === ticket.id)) {
					merged.push(ticket);
				}
			});
			return merged;
		});
	}, [tickets, profile, user, queues]);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;

		// Remove current ticket from notifications when user is viewing it
		if (ticketIdUrl) {
			setNotifications(prevState => {
				const filtered = prevState.filter(t => t.id !== ticketIdUrl);
				if (filtered.length !== prevState.length) {
					console.debug("[NotificationsPopOver] Removed viewed ticket from notifications:", ticketIdUrl);
					return filtered;
				}
				return prevState;
			});
		}
	}, [ticketIdUrl]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.GetSocket(companyId);

    const queueIds = queues.map((q) => q.id);

    const onConnectNotificationsPopover = () => {
		socket.emit("joinNotification");
	}

	const onCompanyTicketNotificationsPopover = (data) => {
      if (data.action === "updateUnread" || data.action === "delete") {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticketId);
					if (ticketIndex !== -1) {
						prevState.splice(ticketIndex, 1);
						return [...prevState];
					}
					return prevState;
				});

				setDesktopNotifications(prevState => {
					const notfiticationIndex = prevState.findIndex(
						n => n.tag === String(data.ticketId)
					);
					if (notfiticationIndex !== -1) {
						prevState[notfiticationIndex].close();
						prevState.splice(notfiticationIndex, 1);
						return [...prevState];
					}
					return prevState;
				});
      }
    };

    const onCompanyAppMessageNotificationsPopover = (data) => {

      // Skip if not a create action or message is already read
      if (data.action !== "create" || data.message.read) return;

      // Skip if message is from agent (fromMe)
      if (data.message.fromMe) return;

      // Supervisor sees all tickets (same as admin for notification purposes)
      const isAdmin = profile === "admin" || profile === "supervisor";
      const ticketQueueId = data.ticket.queueId;
      const ticketUserId = data.ticket.userId;

      // Determine if user should see this notification:
      // 1. Admin/Supervisor sees all
      // 2. Ticket is assigned to this user
      // 3. Ticket is unassigned and in user's queue (or no queue)
      const isAssignedToUser = ticketUserId === user?.id;
      const isInUserQueue = !ticketQueueId || queueIds.includes(ticketQueueId);
      const isUnassignedInQueue = !ticketUserId && isInUserQueue;

      const shouldShowNotification = isAdmin || isAssignedToUser || isUnassignedInQueue;

      if (!shouldShowNotification) return;

      // Update notifications list
      setNotifications(prevState => {
        const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);
        if (ticketIndex !== -1) {
          prevState[ticketIndex] = data.ticket;
          return [...prevState];
        }
        return [data.ticket, ...prevState];
      });

      // Determine if we should show desktop notification and play sound
      const shouldNotNotificate =
        // Don't notify if user is viewing this ticket
        (data.message.ticketId === ticketIdRef.current && document.visibilityState === "visible") ||
        // Don't notify if ticket is assigned to someone else (and not admin)
        (!isAdmin && ticketUserId && ticketUserId !== user?.id) ||
        // Don't notify for groups if setting is disabled
        (data.ticket.isGroup && !soundGroupNotifications);

      if (shouldNotNotificate) return;

      handleNotifications(data);
    }

    socketManager.onConnect(onConnectNotificationsPopover);
    socket.on(`company-${companyId}-ticket`, onCompanyTicketNotificationsPopover);
    socket.on(`company-${companyId}-appMessage`, onCompanyAppMessageNotificationsPopover);

    // If socket is already connected, emit joinNotification immediately
    if (socket.connected) {
      socket.emit("joinNotification");
    }

    return () => {
       // Don't disconnect socket - just remove listeners
       socket.off(`company-${companyId}-ticket`, onCompanyTicketNotificationsPopover);
       socket.off(`company-${companyId}-appMessage`, onCompanyAppMessageNotificationsPopover);
    };
  }, [user, profile, queues, soundGroupNotifications, socketManager]);

  const handleNotifications = (data) => {
    const { message, contact, ticket } = data;

    const body = message.body.startsWith('{"godealvCard"') ? "🪪" : message.body;

    const options = {
      body: `${format(new Date(), "HH:mm")}\n${body}`,
      icon: contact.profilePicUrl,
      tag: ticket.id,
      renotify: true,
    };

    try {
    const notification = new Notification(
      `${i18n.t("tickets.notification.message")} ${contact.name}`,
      options
    );

    notification.onclick = (e) => {
      e.preventDefault();
      window.focus();
      historyRef.current.push(`/tickets/${ticket.uuid}`);
    };

    setDesktopNotifications((prevState) => {
      const notfiticationIndex = prevState.findIndex(
        (n) => n.tag === notification.tag
      );
      if (notfiticationIndex !== -1) {
        prevState[notfiticationIndex] = notification;
        return [...prevState];
      }
      return [notification, ...prevState];
    });
    } catch (e) {
      console.error("Failed to push browser notification");
    }

    soundAlertRef.current();
  };

  const handleClick = () => {
    setIsOpen((prevState) => !prevState);
  };

  const handleClickAway = () => {
    setIsOpen(false);
  };

  const NotificationTicket = ({ children }) => {
    return <div onClick={handleClickAway}>{children}</div>;
  };

  // Title/badge is a DOM side effect — run it when the pending count or
  // app name changes, NOT on every render (it used to be called straight
  // from the JSX).
  useEffect(() => {
    browserNotification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingTickets, theme.appName]);

  const browserNotification = () => {
    // Raviv 2026-05-30: badge should show count of *pending* (ממתינות)
    // tickets one-to-one, not total unread messages.
    const displayCount = pendingTickets.length;
    const numbers = "⓿➊➋➌➍➎➏➐➑➒➓⓫⓬⓭⓮⓯⓰⓱⓲⓳⓴";

    if (displayCount > 0) {
      if (displayCount < 21) {
        document.title = numbers.substring(displayCount, displayCount + 1) + " - " + (theme.appName || "...");
      } else {
        document.title = "(" + displayCount + ")" + " - " + (theme.appName || "...");
      }
    } else {
      document.title = theme.appName || "...";
    }
    return (
      <>
        <Favicon
          animated={true}
          url={ (theme?.appLogoFavicon) ? theme.appLogoFavicon : defaultLogoFavicon }
          alertCount={displayCount > 99 ? 99 : displayCount}
          iconSize={195}
        />
      </>
    );
  };

  return (
    <>

      <IconButton
        onClick={handleClick}

        ref={anchorEl}
        aria-label="Mostrar Notificações"
        variant="contained"

      >
          <ChatIcon style={{ color: "white" }} />
        {notifications.length > 0 ?<Badge variant="dot" color="secondary"
        style={{marginTop: "-25px"}}>
        </Badge>: ""}
      </IconButton>
      <Popover
        disableScrollLock
        open={isOpen}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        classes={{ paper: classes.popoverPaper }}
        onClose={handleClickAway}
      >
        <List dense className={classes.tabContainer}>
          {notifications.length === 0 ? (
            <ListItem>
              <ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
            </ListItem>
          ) : (
            notifications.map((ticket) => (
              <NotificationTicket key={ticket.id}>
                <TicketListItem ticket={ticket} groupActionButtons={!showTabGroups} />
              </NotificationTicket>
            ))
          )}
        </List>
      </Popover>
    </>
  );
};

// Mounted permanently in the AppBar — memo keeps it out of shell re-renders.
export default React.memo(NotificationsPopOver);
